import { api, API_BASE_URL, getAuthToken } from './core';

/**
 * ThunderContent-Anbindung im Office.
 *
 * Alles läuft über die eigene Proxy-Route im Backend (`/api/content-hub/hub/*`,
 * per nginx → `/api/v1/office/content-hub/hub/*`). Der Site-Key liegt dort und
 * erreicht den Browser nie.
 *
 * Die Hooks und der Social-Bereich des Pakets (`@/vendor/thundercontent-hub`)
 * bekommen `CONTENT_HUB_BASE` und `hubHeaders` — das Paket kennt keine Cookies
 * des Office, das Token muss ausdrücklich mit. Themenplan und Zahlen hat das
 * Paket (noch) nicht im Browser-Client; die holt dieser Client über denselben
 * Proxy.
 */

export const CONTENT_HUB_BASE = `${API_BASE_URL}/api/content-hub`;

/** Wird bei jeder Anfrage neu gelesen — ein erneuertes Token ist kein Problem. */
export const hubHeaders = (): Record<string, string> => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export interface ContentHubStatus {
  configured: boolean;
  /** Namen fehlender Umgebungsvariablen — nie deren Werte. */
  missing: string[];
  tenant_matches: boolean;
  hub_endpoint: string;
  website_url: string;
  announce_on_startup: boolean;
  package_version: string;
  capabilities: {
    categories: string[];
    conversionGoals?: Record<string, string>;
  } & Record<string, unknown>;
}

export interface HandshakeResult {
  site_id: number;
  site_name: string;
  status: string;
  compatible: boolean;
  message: string;
}

export interface HubTopic {
  id: number;
  title: string;
  angle: string;
  primary_keyword: string;
  cluster: string;
  /** concept | wish | event */
  source: string;
  position: number;
  scheduled_for: string | null;
  status: string;
  article_id: number | null;
}

export interface HubPlan {
  id: number;
  period_month: string;
  /** review | approved | … */
  status: string;
  displacement_note: string;
  review_due_at: string | null;
  approved_at: string | null;
  topics: HubTopic[];
}

export interface HubMetricArticle {
  slug: string;
  article_id: number | null;
  title: string;
  published_at: string | null;
  views_window: number;
  views_total: number;
  reads_total: number;
  read_ratio: number | null;
  by_referrer: Record<string, number>;
  conversions_window?: number;
  conversions_total?: number;
}

export interface HubConversionGoal {
  goal: string;
  label: string;
  window: number;
  /** Davon nach einem Beitrag. */
  assisted_window: number;
  total: number;
  assisted_total: number;
}

/** Was die Beiträge gebracht haben — aus der eigenen, cookiefreien Zählung. */
export interface HubMetrics {
  days: number;
  date_from: string;
  date_to: string;
  views_window: number;
  views_total: number;
  /** Erster Tag mit Daten — die Zählung misst erst ab dem Einschalten. */
  counting_since: string | null;
  daily: { day: string; views: number }[];
  articles: HubMetricArticle[];
  conversions_enabled?: boolean;
  conversions_window?: number;
  conversions_assisted_window?: number;
  conversions_by_goal?: HubConversionGoal[];
}

const HUB = '/api/content-hub/hub';

export const contentHubApi = {
  status: () => api.get<ContentHubStatus>('/api/content-hub/status'),

  /** Achtung: Schaltet die Site in ThunderContent von „setup“ auf „active“. */
  handshake: () => api.post<HandshakeResult>('/api/content-hub/handshake', {}),

  plans: () => api.get<HubPlan[]>(`${HUB}/plans`),

  approvePlan: (id: number, approvedBy: string) =>
    api.post<HubPlan>(`${HUB}/plans/${id}/approve`, { approved_by: approvedBy, via: 'hub' }),

  /** Themen streichen und/oder Freitext — der Plan geht aus der Frist heraus. */
  requestPlanChanges: (id: number, feedback: string, rejectTopicIds: number[], requestedBy: string) =>
    api.post<HubPlan>(`${HUB}/plans/${id}/changes`, {
      feedback,
      reject_topic_ids: rejectTopicIds,
      requested_by: requestedBy,
    }),

  metrics: (days = 30) => api.get<HubMetrics>(`${HUB}/metrics?days=${days}`),
};
